import dayjs from 'dayjs';

/** One Tagebuch entry reduced to what the one-per-day rule needs. */
export interface DiaryDayRow {
    id: string;
    projectId: string;
    recordedAt: string;
}

/**
 * The day an entry belongs to ('YYYY-MM-DD'), or '' when it carries no usable
 * date. The stored value is a full timestamp, but the Tagebuch is kept per DAY.
 */
export function diaryDay(recordedAt: string): string {
    if (!recordedAt) {
        return '';
    }
    const parsed = dayjs(recordedAt);
    return parsed.isValid() ? parsed.format('YYYY-MM-DD') : '';
}

/**
 * The entry that already holds `recordedAt`'s day in this project, or null.
 * `excludeId` skips the row being edited. Scoped to the project rather than the
 * family on purpose: each Teilmaßnahme keeps its own Tagebuch.
 */
export function findDiaryOnDay(
    diaries: readonly DiaryDayRow[],
    projectId: string,
    recordedAt: string,
    excludeId: string
): DiaryDayRow | null {
    const day = diaryDay(recordedAt);
    if (day === '') {
        return null;
    }
    return (
        diaries.find(
            (diary) =>
                diary.id !== excludeId &&
                diary.projectId === projectId &&
                diaryDay(diary.recordedAt) === day
        ) ?? null
    );
}

/**
 * Deterministic, client-side detection of days recorded twice — the collision
 * the form's save-time check cannot see, because it happens when two devices
 * each write the same day offline and both sync.
 *
 * Groups by project + day; ALL members of a collision are flagged (neither is
 * canonical). Entries without a usable date are skipped. Accepts entries of
 * several projects, so the cross-project Auswertung can use the same rule.
 *
 * Returns a map from each colliding entry's id to its day ('YYYY-MM-DD').
 */
export function computeDiaryDuplicates(
    diaries: readonly DiaryDayRow[]
): Map<string, string> {
    const byDay = new Map<string, DiaryDayRow[]>();
    for (const diary of diaries) {
        const day = diaryDay(diary.recordedAt);
        if (day === '') {
            continue;
        }
        const key = `${diary.projectId}|${day}`;
        const group = byDay.get(key);
        if (group) {
            group.push(diary);
        } else {
            byDay.set(key, [diary]);
        }
    }

    const duplicates = new Map<string, string>();
    for (const group of byDay.values()) {
        if (group.length < 2) {
            continue;
        }
        for (const diary of group) {
            duplicates.set(diary.id, diaryDay(diary.recordedAt));
        }
    }
    return duplicates;
}
